import { useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../api'
import { avecAlpha } from '../../lib/couleurs'

/**
 * Mes certifications — preuve → vérification → badge (v7.1 sujet 4, ADR-056).
 * - UPLOAD (analyse de sol, certificat) : dépôt de document → EN_COURS → vérif Manrina.
 * - ENGAGEMENT (charte pesticides) : case cochée → VALIDEE immédiatement.
 * Le badge côté client n'apparaît que si VALIDEE et non expirée.
 * Rattachement hybride : un document peut concerner un terrain précis (siteNom).
 */

const CERT_COLORS = {
  ZERO_CHLORDECONE: 'rgb(var(--c-green))',
  BIO_ECOCERT: 'rgb(var(--c-teal))',
  ENGAGEMENT_PESTICIDES: '#0a8a5f',
}

const STATUTS = {
  VALIDEE: { l: 'Validée', c: '#0a8a5f', e: '✓' },
  EN_COURS: { l: "En cours d'examen", c: 'rgb(var(--c-primary))', e: '⏳' },
  REFUSEE: { l: 'À renouveler', c: '#d9534f', e: '↺' },
  EXPIREE: { l: 'Expirée', c: '#d9534f', e: '↺' },
  NON_DEMANDEE: { l: 'Non demandée', c: 'rgb(var(--c-green) / 0.4)', e: '' },
}

const formatMoisAnnee = (d) => {
  if (!d) return null
  const date = new Date(d)
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
}

function DepotOverlay({ cert, onClose, onDone }) {
  const estEngagement = cert.preuveType === 'ENGAGEMENT'
  const [coche, setCoche] = useState(false)
  const [file, setFile] = useState(null)
  const [siteNom, setSiteNom] = useState(cert.site?.nom || '')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const color = CERT_COLORS[cert.code] || 'rgb(var(--c-teal))'

  const submit = async () => {
    setSending(true)
    setError(null)
    try {
      if (estEngagement) {
        await api.producer.acceptCertifEngagement(cert.code)
      } else {
        await api.producer.submitCertifDocument(cert.code, file, siteNom.trim() || undefined)
      }
      onDone()
    } catch (err) {
      setError(err.message || "Erreur lors de l'envoi")
      setSending(false)
    }
  }

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[120] flex items-center justify-center p-6"
      style={{ background: 'rgb(4 25 24 / 0.5)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-surface w-full max-w-[380px] max-h-[80vh] overflow-y-auto rounded-[28px] p-5 animate-fade-up"
        style={{ background: 'rgb(var(--c-creme))' }}
      >
        <div className="flex items-center gap-3 mb-3.5">
          <span
            className="w-11 h-11 rounded-2xl flex-shrink-0 flex items-center justify-center text-[21px]"
            style={{ background: avecAlpha(color, 0.1), border: `1px solid ${avecAlpha(color, 0.25)}` }}
          >{cert.emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-[20px] text-ink">{cert.nom}</p>
            <p className="text-[12px] text-ink/55">{cert.preuveLabel}</p>
          </div>
        </div>

        {estEngagement ? (
          <>
            {/* ENGAGEMENT : charte + clause + case à cocher (validée immédiatement) */}
            <div className="rounded-2xl p-4 mb-3.5" style={{ background: 'rgb(var(--c-teal) / 0.06)', border: '1px solid rgb(var(--c-teal) / 0.15)' }}>
              <p className="text-[13px] leading-relaxed text-ink/80">
                Je m'engage à ne pas utiliser de <strong>pesticides de synthèse</strong> sur ma production proposée via Manrina.
              </p>
              <p className="text-[12.5px] leading-relaxed text-ink/70 mt-2.5">
                J'autorise Manrina à faire <strong>analyser mes produits</strong> à tout moment pour le vérifier.
                En cas de manquement constaté, Manrina peut <strong>rompre la collaboration sans préavis</strong>.
              </p>
            </div>
            <button
              onClick={() => setCoche((v) => !v)}
              className="w-full rounded-2xl p-3.5 flex items-center gap-3 text-left"
              style={{
                background: coche ? 'rgb(var(--c-teal) / 0.1)' : 'rgba(255,255,255,0.5)',
                border: coche ? '1.5px solid rgb(var(--c-teal))' : '1px solid rgb(var(--c-green) / 0.12)',
              }}
            >
              <span
                className="w-6 h-6 rounded-[7px] flex-shrink-0 flex items-center justify-center text-white text-sm font-extrabold"
                style={coche ? { background: 'rgb(var(--c-teal))' } : { border: '2px solid rgb(var(--c-green) / 0.25)' }}
              >{coche ? '✓' : ''}</span>
              <span className="flex-1 text-[13px] font-bold text-ink">J'ai lu et j'accepte cet engagement</span>
            </button>
            <button
              disabled={!coche || sending}
              onClick={submit}
              className="mt-3 w-full py-3.5 rounded-2xl text-[14.5px] font-extrabold text-creme"
              style={{ background: coche ? 'rgb(var(--c-green))' : 'rgb(var(--c-green) / 0.18)' }}
            >
              {sending ? 'Envoi...' : 'Valider mon engagement'}
            </button>
          </>
        ) : (
          <>
            {/* PREUVE : document (PDF ou photo) + terrain concerné (hybride ADR-056) */}
            <p className="text-[12.5px] text-ink/60 leading-relaxed mb-3.5">
              Joignez votre document (PDF ou photo). L'équipe Manrina le vérifie sous 48-72h.
              Le badge apparaîtra sur votre fiche une fois validé.
            </p>
            <label className="block mb-3.5">
              <span className="text-[11.5px] font-bold text-ink/60 block mb-1.5">
                Terrain concerné {cert.code === 'ZERO_CHLORDECONE' ? '(recommandé pour une analyse de sol)' : '(optionnel)'}
              </span>
              <input
                value={siteNom}
                onChange={(e) => setSiteNom(e.target.value)}
                placeholder="Ex : Parcelle Morne-Rouge"
                className="w-full text-[16px] px-3.5 py-3 rounded-[13px] text-ink"
                style={{ border: '1px solid rgb(var(--c-green) / 0.15)', background: 'rgba(255,255,255,0.75)' }}
              />
              <span className="text-[10.5px] text-ink/45 block mt-1">
                Laissez vide si toute l'exploitation est concernée.
              </span>
            </label>
            <label
              className="w-full py-3.5 rounded-2xl text-[14px] font-extrabold text-ink flex items-center justify-center gap-2 cursor-pointer"
              style={{ border: '1.5px dashed rgb(var(--c-primary))', background: 'rgb(var(--c-primary) / 0.08)' }}
            >
              📎 {file ? file.name : 'Joindre un document'}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <button
              disabled={!file || sending}
              onClick={submit}
              className="mt-3 w-full py-3.5 rounded-2xl text-[14.5px] font-extrabold text-creme"
              style={{ background: file ? 'rgb(var(--c-green))' : 'rgb(var(--c-green) / 0.18)' }}
            >
              {sending ? 'Envoi...' : 'Envoyer pour vérification'}
            </button>
          </>
        )}

        {error && <p className="mt-2.5 text-[12px] text-[#d9534f] text-center">{error}</p>}
        <button onClick={onClose} className="mt-2.5 w-full py-3 text-[13px] font-bold text-ink/50">
          Annuler
        </button>
      </div>
    </div>,
    document.body
  )
}

function CertifsSection({ certifs, onRefresh }) {
  const [depot, setDepot] = useState(null) // certification en cours de dépôt/engagement

  if (!certifs?.length) return null

  return (
    <div className="mt-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 px-0.5" style={{ color: 'rgb(var(--c-teal))' }}>
        Mes certifications
      </p>
      <p className="text-[11.5px] text-ink/50 leading-relaxed mb-3 px-0.5">
        🛡️ Un badge n'apparaît côté client qu'après vérification de votre preuve par l'équipe Manrina.
      </p>
      <div className="flex flex-col gap-2.5">
        {certifs.map((c) => {
          const statut = c.statut || 'NON_DEMANDEE'
          const st = STATUTS[statut] || STATUTS.NON_DEMANDEE
          const color = CERT_COLORS[c.code] || 'rgb(var(--c-teal))'
          const estEngagement = c.preuveType === 'ENGAGEMENT'
          const validite = formatMoisAnnee(c.dateValidite)
          return (
            <div key={c.code} className="glass-surface rounded-[18px] p-3.5">
              <div className="flex items-center gap-3">
                <span
                  className="w-10 h-10 rounded-[13px] flex-shrink-0 flex items-center justify-center text-[19px]"
                  style={{ background: avecAlpha(color, 0.1), border: `1px solid ${avecAlpha(color, 0.25)}` }}
                >{c.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-bold text-ink">{c.nom}</p>
                  <p className="text-[11px] text-ink/50">
                    {c.preuveLabel}
                    {statut === 'VALIDEE' && validite ? ` · valide jusqu'au ${validite}` : ''}
                    {c.site?.nom ? ` · ${c.site.nom}` : ''}
                  </p>
                </div>
                <span
                  className="text-[10.5px] font-extrabold px-2.5 py-1.5 rounded-full flex-shrink-0"
                  style={{ color: st.c, background: statut === 'NON_DEMANDEE' ? 'rgb(var(--c-green) / 0.08)' : avecAlpha(st.c, 0.1) }}
                >{st.e} {st.l}</span>
              </div>

              {statut === 'NON_DEMANDEE' && (
                <button
                  onClick={() => setDepot(c)}
                  className="mt-2.5 w-full py-2.5 rounded-[13px] text-[13px] font-extrabold text-ink"
                  style={{ background: 'rgb(var(--c-primary))' }}
                >{estEngagement ? "M'engager ›" : 'Faire vérifier ›'}</button>
              )}
              {statut === 'EN_COURS' && (
                <p className="mt-2.5 text-[11.5px] text-ink/55 leading-relaxed rounded-xl px-3 py-2.5"
                  style={{ background: 'rgb(var(--c-primary) / 0.08)' }}>
                  📎 {c.documentNom || 'Document'} reçu — vérification sous 48-72h.
                </p>
              )}
              {statut === 'VALIDEE' && (
                <button
                  onClick={() => setDepot(c)}
                  className="mt-2 text-[11.5px] font-bold text-ink/50"
                >{estEngagement ? 'Revoir mon engagement ›' : 'Mettre à jour le document ›'}</button>
              )}
              {(statut === 'REFUSEE' || statut === 'EXPIREE') && (
                <>
                  {c.motifRefus && (
                    <p className="mt-2.5 text-[11.5px] text-[#d9534f]/90 leading-relaxed rounded-xl px-3 py-2.5"
                      style={{ background: 'rgb(217 83 79 / 0.08)' }}>
                      {c.motifRefus}
                    </p>
                  )}
                  <button
                    onClick={() => setDepot(c)}
                    className="mt-2.5 w-full py-2.5 rounded-[13px] text-[13px] font-extrabold text-ink"
                    style={{ background: 'rgb(var(--c-primary))' }}
                  >{statut === 'EXPIREE' ? 'Renouveler ›' : 'Redéposer un document ›'}</button>
                </>
              )}
            </div>
          )
        })}
      </div>

      {depot && (
        <DepotOverlay
          cert={depot}
          onClose={() => setDepot(null)}
          onDone={() => { setDepot(null); onRefresh?.() }}
        />
      )}
    </div>
  )
}

export default CertifsSection
